import React from "react";
import {observer} from "mobx-react-lite";
import {
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow
} from "@mui/material";
import {Close} from "@mui/icons-material";
import type {PartHistoryDialogStore} from "./PartHistoryDialogStore";
import {partHistoryReasonToString, partHistoryRelatedObjectTypeToString, PartHistoryRelatedObjectType} from "~/services/classes/PartHistory";
import {categoryExportString} from "~/services/classes/Category.ts";
import {AuthorChip} from "~/pages/taxonomy-editor/approval/component/AuthorChip.tsx";

type Props = {
    store: PartHistoryDialogStore
}

export const PartHistoryDialog: React.FC<Props> = observer(({store}) => {
    if (!store.isOpen) return null;

    const {result, busy, error} = store.historyRM;

    return <Dialog open onClose={() => store.close()} maxWidth="md" fullWidth>
        <DialogTitle>
            Part history
            <IconButton onClick={() => store.close()} size="small"
                        style={{position: 'absolute', right: 8, top: 8}} aria-label="Close">
                <Close fontSize="small"/>
            </IconButton>
        </DialogTitle>
        <DialogContent>
            {busy && <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <CircularProgress size={16}/> Loading…
            </div>}
            {error && !busy &&
                <div>Failed to load part history.</div>}
            {result && !busy && result.length === 0 &&
                <div>No history recorded for this part.</div>}
            {result && result.length > 0 &&
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Reason</TableCell>
                            <TableCell>Related object</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {result.map(entry =>
                            <TableRow key={entry.id}>
                                <TableCell>{new Date(entry.created_at).toLocaleString()}</TableCell>
                                <TableCell>{categoryExportString(entry.category)}</TableCell>
                                <TableCell>{partHistoryReasonToString(entry.reason)}</TableCell>
                                <TableCell>
                                    {entry.related_object_type === PartHistoryRelatedObjectType.User && entry.related_object_id
                                        ? <AuthorChip authorId={entry.related_object_id}/>
                                        : <>
                                            {partHistoryRelatedObjectTypeToString(entry.related_object_type)}
                                            {entry.related_object_id ? ` #${entry.related_object_id}` : ''}
                                        </>}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>}
        </DialogContent>
    </Dialog>
});
